/**
 * Slide Creation — loading a saved AI Crosstab into a computed table.
 *
 * This is the input half of the "From crosstabs" path. It reuses the crosstab
 * workspace's own compute so a report is built from exactly the numbers the
 * workspace shows — rather than a second, drifting implementation.
 *
 * The output `ComputedTable` is what the `Question[]` adapter will consume once
 * the canvas editor lands (see docs/pdlc/slide-creation-migration.md, B3).
 */

import type { ComputedTable } from '@/app/(private)/ai/components/Crosstabs/data';
import { stampOf, UUID_RE } from '@/lib/crosstab/saved-survey-scope';

import { fetchAiCrosstabState } from './crosstab-source';

/** Row/banner selection persisted on a saved AI crosstab layout. */
export interface LayoutSelection {
    activeRows: string[];
    activeBanner: string[];
    /**
     * M4S-5777: the surveys the crosstab was saved on (`ai_layout.state.surveyScope`, M4S-5646),
     * so the deck is built on the surveys the workspace shows. Absent for a crosstab on its whole
     * project, which stores none. A Duplicate's copied stamp counts: it is the list the content
     * was copied with. Spec: docs/pdlc/crosstabs-survey-load.md §4.3.
     */
    surveyIds?: string[];
    /**
     * M4S-5777 (X11): the saved crosstab's key. When set, the server reads the record itself and
     * computes it with everything it holds (variables, filters, weighting, statistics, surveys),
     * so the deck has the workspace's numbers. The fields above are then only the "is there
     * anything to build" check.
     */
    layoutKey?: string;
}

/**
 * Fetches a crosstab's saved row/banner selection.
 *
 * Returns null when the crosstab carries no AI state, or state that selects
 * nothing — the caller surfaces that rather than silently computing an empty
 * table. Both cases mean "there is nothing here to build a deck from".
 */
export async function fetchLayoutSelection(crosstabId: string): Promise<LayoutSelection | null> {
    const state = await fetchAiCrosstabState(crosstabId);
    if (!state) return null;

    const activeRows = Array.isArray(state.activeRows) ? state.activeRows.map(String) : [];
    const activeBanner = Array.isArray(state.activeBanner) ? state.activeBanner.map(String) : [];
    if (activeRows.length === 0 && activeBanner.length === 0) return null;

    const saved = stampOf(state)?.surveyIds ?? [];
    const layoutKey = UUID_RE.test(crosstabId) ? crosstabId : undefined;
    return {
        activeRows,
        activeBanner,
        ...(saved.length ? { surveyIds: saved } : {}),
        ...(layoutKey ? { layoutKey } : {}),
    };
}

/**
 * Fetches a project's questions and responses and computes the saved crosstab.
 *
 * `projectId` must be the numeric `salesforce_project_id`. With no surveys named (by
 * `surveyIds`, else the crosstab's own saved list on `selection`), the route resolves it to that
 * project's surveys server-side, as before.
 */
export async function loadCrosstabTable(
    projectId: string,
    selection: LayoutSelection,
    surveyIds?: string[],
): Promise<ComputedTable> {
    /**
     * Tabulated on the server.
     *
     * This path computes once from a selection already saved on the layout and never
     * changes it, so there is no reason to ship every raw response here to fold it
     * down — that was forty-odd megabytes for a tracker-sized project, and the
     * tabulation ran on whatever machine opened the deck. The workspace still gets
     * raw responses from the sibling route, because it recomputes as the user works.
     */
    const res = await fetch(`/api/crosstab/project/${encodeURIComponent(projectId)}/computed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // M4S-5777 (X11): a saved crosstab is named by its key and computed from its record.
        body: JSON.stringify(
            selection.layoutKey && !surveyIds
                ? { layoutKey: selection.layoutKey }
                : {
                      surveys: surveyIds ?? selection.surveyIds ?? [],
                      activeRows: selection.activeRows,
                      activeBanner: selection.activeBanner,
                  },
        ),
    });
    const data = (await res.json().catch(() => null)) as {
        table?: ComputedTable;
        error?: string;
    } | null;

    if (!res.ok || !data || data.error || !data.table) {
        throw new Error(data?.error ?? `Could not load crosstab data (${res.status})`);
    }

    return data.table;
}
